import React, { useEffect, useMemo, useState } from 'react';
import { X, ArrowRight, Calendar, Clock, Video, CheckCircle, Briefcase, GraduationCap, Users, Shield, Search, FileText } from 'lucide-react';
import { careerCategories, experts, journeySteps, organisationRoles } from '../data/platformData';
import { counsellingService } from '../services/counsellingService';

const API_TODO = {
  organisation: 'POST /api/organisation-applications',
  booking: 'POST /api/consultations',
  careerInterest: 'POST /api/career-library/interests',
};

const Panel = ({ title, eyebrow, children, onClose, wide = false }) => (
  <div className="platform-overlay" role="dialog" aria-modal="true" aria-label={title}>
    <div className={`platform-panel ${wide ? 'platform-panel-wide' : ''}`}>
      <button className="platform-close" onClick={onClose} aria-label="Close"><X size={18} /></button>
      <div className="section-label">{eyebrow}</div>
      <h2 className="platform-title">{title}</h2>
      {children}
    </div>
  </div>
);

const Field = ({ label, type = 'text', value, onChange, required = true }) => (
  <label className="platform-field">
    <span>{label}</span>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} />
  </label>
);

const RoleOnboarding = ({ onClose }) => {
  const [activeRole, setActiveRole] = useState(organisationRoles[0]);
  const [form, setForm] = useState({ name: '', email: '', phone: '', city: '', experience: '', note: '' });
  const [submitted, setSubmitted] = useState(false);

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const submit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <Panel title="Join Our Organisation" eyebrow="Expert Onboarding" onClose={onClose} wide>
      <div className="platform-role-tabs">
        {organisationRoles.map((role) => (
          <button key={role.id} className={activeRole.id === role.id ? 'active' : ''} onClick={() => { setActiveRole(role); setSubmitted(false); }}>
            {role.title}
          </button>
        ))}
      </div>

      <div className="platform-two-col">
        <div>
          <p className="platform-lead">{activeRole.summary}</p>
          {[
            ['Eligibility', activeRole.eligibility],
            ['Responsibilities', activeRole.responsibilities],
            ['Benefits', activeRole.benefits],
          ].map(([heading, items]) => (
            <div className="platform-info-block" key={heading}>
              <h3>{heading}</h3>
              {items.map((item) => <div className="platform-check" key={item}><CheckCircle size={14} />{item}</div>)}
            </div>
          ))}
          <div className="platform-api-note">Backend integration point: {API_TODO.organisation}</div>
        </div>

        {submitted ? (
          <div className="platform-success">
            <CheckCircle size={38} />
            <h3>Application received</h3>
            <p>Thank you, {form.name || 'expert'}. The MindVrik team will review your {activeRole.title} application and contact you for verification.</p>
            <button className="btn-primary" onClick={() => setSubmitted(false)}>Submit Another</button>
          </div>
        ) : (
          <form className="platform-form" onSubmit={submit}>
            <Field label="Full name" value={form.name} onChange={(v) => update('name', v)} />
            <Field label="Email" type="email" value={form.email} onChange={(v) => update('email', v)} />
            <Field label="Phone" value={form.phone} onChange={(v) => update('phone', v)} />
            <Field label="City" value={form.city} onChange={(v) => update('city', v)} />
            <Field label="Years of experience" value={form.experience} onChange={(v) => update('experience', v)} />
            <label className="platform-field platform-field-full">
              <span>Why do you want to join MindVrik?</span>
              <textarea value={form.note} onChange={(e) => update('note', e.target.value)} required />
            </label>
            <button className="btn-primary" type="submit">Submit Application <ArrowRight size={15} /></button>
          </form>
        )}
      </div>
    </Panel>
  );
};

const ExpertProfile = ({ expert, onClose, onBook }) => (
  <Panel title={expert.name} eyebrow="Expert Profile" onClose={onClose}>
    <div className="expert-profile">
      <img src={expert.image} alt={`${expert.name} profile`} />
      <div>
        <h3>{expert.role}</h3>
        <p>{expert.bio}</p>
        <div className="profile-grid">
          <span>Qualification<strong>{expert.qualification}</strong></span>
          <span>Experience<strong>{expert.experience}</strong></span>
          <span>Languages<strong>{expert.languages.join(', ')}</strong></span>
          <span>Availability<strong>{expert.availability.join(' · ')}</strong></span>
        </div>
        <div className="platform-chip-row">{expert.specializations.map((s) => <span key={s}>{s}</span>)}</div>
        <button className="btn-primary" onClick={() => onBook(expert)}>Book Consultation <ArrowRight size={15} /></button>
      </div>
    </div>
  </Panel>
);

const BookingFlow = ({ expert, onClose }) => {
  const [step, setStep] = useState(0);
  const [booking, setBooking] = useState({ expert: expert || experts[0], date: '', time: '', type: 'Phone Counselling' });
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [error, setError] = useState('');
  const dates = useMemo(() => counsellingService.getBookableDates(14), []);
  const steps = ['Expert', 'Date', 'Time', 'Type', 'Summary', 'Confirmation'];

  useEffect(() => {
    if (!booking.date || !booking.expert?.id) {
      setSlots([]);
      return;
    }

    let active = true;
    setLoadingSlots(true);
    setError('');
    counsellingService.fetchSlots({ expertId: booking.expert.id, date: booking.date })
      .then((result) => {
        if (active) setSlots(result.slots || []);
      })
      .catch((err) => {
        if (active) setError(err.message || 'Could not load available slots.');
      })
      .finally(() => {
        if (active) setLoadingSlots(false);
      });

    return () => {
      active = false;
    };
  }, [booking.date, booking.expert]);

  const confirmBooking = async () => {
    setError('');
    try {
      const result = await counsellingService.createBooking({
        expert: booking.expert,
        date: booking.date,
        time: booking.time,
        sessionType: booking.type,
      });
      setBookingResult(result);
      setStep(5);
    } catch (err) {
      setError(err.message || 'Could not confirm booking.');
    }
  };

  const next = () => {
    if (step === 4) {
      confirmBooking();
      return;
    }
    setStep((s) => Math.min(s + 1, steps.length - 1));
  };
  const select = (key, value) => setBooking((current) => ({ ...current, [key]: value }));

  return (
    <Panel title="Book Consultation" eyebrow="Guided Booking" onClose={onClose} wide>
      <div className="booking-steps">{steps.map((s, i) => <span key={s} className={i <= step ? 'active' : ''}>{s}</span>)}</div>
      <div className="booking-body">
        {step === 0 && <div className="platform-card-grid">{experts.map((e) => <button key={e.id} className={booking.expert.id === e.id ? 'active' : ''} onClick={() => setBooking((current) => ({ ...current, expert: e, date: '', time: '' }))}><img src={e.image} alt="" /><strong>{e.name}</strong><span>{e.role}</span></button>)}</div>}
        {step === 1 && <div className="choice-grid">{dates.map((d) => <button key={d} className={booking.date === d ? 'active' : ''} onClick={() => setBooking((current) => ({ ...current, date: d, time: '' }))}><Calendar size={15} />{d}</button>)}</div>}
        {step === 2 && (
          <div>
            {loadingSlots && <p className="platform-lead">Loading available slots...</p>}
            {!loadingSlots && slots.length === 0 && <p className="platform-lead">No slots available for this date. Please choose another date.</p>}
            <div className="choice-grid">{slots.map((t) => <button key={t} className={booking.time === t ? 'active' : ''} onClick={() => select('time', t)}><Clock size={15} />{t}</button>)}</div>
          </div>
        )}
        {step === 3 && <div className="choice-grid">{['Phone Counselling', 'Parent + Student Phone Session', 'Career Roadmap Review'].map((t) => <button key={t} className={booking.type === t ? 'active' : ''} onClick={() => select('type', t)}><Video size={15} />{t}</button>)}</div>}
        {step === 4 && <div className="platform-summary"><h3>Review Booking</h3><p><strong>Expert:</strong> {booking.expert.name}</p><p><strong>Date:</strong> {booking.date || 'Select a date'}</p><p><strong>Time:</strong> {booking.time || 'Select a time'}</p><p><strong>Session:</strong> {booking.type}</p><p><strong>Mode:</strong> Phone call</p><div className="platform-api-note">Booking will be saved in Supabase. Payment can be attached when Razorpay is ready.</div></div>}
        {step === 5 && <div className="platform-success"><CheckCircle size={40} /><h3>Consultation confirmed</h3><p>Your booking with {bookingResult?.expertName || booking.expert.name} is confirmed for {bookingResult?.date || booking.date} at {bookingResult?.time || booking.time}. The session mode is phone call.</p></div>}
      </div>
      {error && <div className="checkout-message error">{error}</div>}
      {step < 5 && <button className="btn-primary" onClick={next} disabled={(step === 1 && !booking.date) || (step === 2 && !booking.time) || (step === 4 && !booking.time)}> {step === 4 ? 'Confirm Booking' : 'Continue'} <ArrowRight size={15} /></button>}
    </Panel>
  );
};

const CareerLibrary = ({ onClose }) => {
  const [active, setActive] = useState(careerCategories[0]);
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => careerCategories.filter((c) => `${c.title} ${c.careers.join(' ')}`.toLowerCase().includes(query.toLowerCase())), [query]);

  return (
    <Panel title="Career Library" eyebrow="Career Explorer" onClose={onClose} wide>
      <div className="career-search"><Search size={16} /><input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search careers, categories, or skills" /></div>
      <div className="career-layout">
        <div className="career-grid">
          {filtered.map((category) => (
            <button key={category.id} className={active.id === category.id ? 'active' : ''} onClick={() => setActive(category)}>
              <Briefcase size={18} />
              <strong>{category.title}</strong>
              <span>{category.careers.length} pathways</span>
            </button>
          ))}
        </div>
        <div className="career-detail">
          <h3>{active.title}</h3>
          <p>{active.detail}</p>
          <div className="platform-chip-row">{active.careers.map((career) => <span key={career}>{career}</span>)}</div>
          <button className="btn-primary">Save Interest <ArrowRight size={15} /></button>
          <div className="platform-api-note">Backend integration point: {API_TODO.careerInterest}</div>
        </div>
      </div>
      <CareerJourneyTimeline compact />
    </Panel>
  );
};

const CareerJourneyTimeline = ({ compact = false, onSelectStep }) => (
  <div className={compact ? 'journey-timeline compact' : 'journey-timeline'}>
    {journeySteps.map(([grade, goal]) => {
      const Tag = onSelectStep ? 'button' : 'div';
      return (
      <Tag
        className={`journey-node ${onSelectStep ? 'journey-node-clickable' : ''}`}
        key={grade}
        type={onSelectStep ? 'button' : undefined}
        onClick={onSelectStep ? () => onSelectStep({ grade, goal }) : undefined}
      >
        <strong>{grade}</strong>
        <span>{goal}</span>
      </Tag>
    );
    })}
  </div>
);

const ParentDashboard = ({ onClose }) => (
  <Panel title="Parent Dashboard Preview" eyebrow="Parent View" onClose={onClose} wide>
    <div className="parent-dashboard">
      <div className="parent-main">
        <h3>Aryan's Guidance Snapshot</h3>
        <div className="parent-score">82<span>Readiness Score</span></div>
        <div className="platform-chip-row"><span>Science Fit</span><span>Aptitude Strong</span><span>Needs Stress Support</span></div>
      </div>
      {[
        ['Next Step', 'Book career roadmap review', <Calendar size={18} />],
        ['Report', 'AI advisory report ready', <FileText size={18} />],
        ['Privacy', 'Secure parent-first records', <Shield size={18} />],
        ['Counselor', 'Expert guidance available', <Users size={18} />],
      ].map(([title, text, icon]) => <div className="parent-tile" key={title}>{icon}<strong>{title}</strong><span>{text}</span></div>)}
    </div>
  </Panel>
);

const AssessmentDetails = ({ onClose }) => (
  <Panel title="Assessment Details" eyebrow="MindVrik Assessment" onClose={onClose}>
    <p className="platform-lead">A 1.5-2 hour structured assessment covering aptitude mapping, learning style, multiple intelligence, emotional indicators, stream fit, and college pathway readiness.</p>
    <div className="platform-info-block">
      {['Pause and continue later', 'Progress automatically saved', 'AI advisory report generated', 'Expert counseling included as per plan'].map((item) => <div className="platform-check" key={item}><CheckCircle size={14} />{item}</div>)}
    </div>
  </Panel>
);

const MindQPlatformViews = ({ view, selectedExpert, onClose, onOpenBooking }) => {
  if (!view) return null;
  if (view === 'join') return <RoleOnboarding onClose={onClose} />;
  if (view === 'expert-profile') return <ExpertProfile expert={selectedExpert || experts[0]} onClose={onClose} onBook={onOpenBooking} />;
  if (view === 'booking') return <BookingFlow expert={selectedExpert} onClose={onClose} />;
  if (view === 'careers') return <CareerLibrary onClose={onClose} />;
  if (view === 'parent-dashboard') return <ParentDashboard onClose={onClose} />;
  if (view === 'assessment') return <AssessmentDetails onClose={onClose} />;
  return null;
};

export { CareerJourneyTimeline };
export default MindQPlatformViews;
